import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EnvelopeIcon, MapPinIcon, PhoneIcon } from '@heroicons/react/24/outline';
import API_BASE_URL from '../../../config';
import { trackGenerateLead } from '../../../lib/analytics';
import SectionSurface from '../shared/SectionSurface';
import {
  CONTACT_HERO,
  CONTACT_SERVICE_OPTIONS,
  DEFAULT_CONTACT_SERVICE,
  getContactServiceBySlug,
  resolveContactServiceValue,
} from '../../../content/enterpriseMessaging';

const defaultPlaceholder = () =>
  CONTACT_SERVICE_OPTIONS.find((o) => o.value === DEFAULT_CONTACT_SERVICE)?.messagePlaceholder || '';

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  role: '',
  companySize: '',
  service: '',
  inquiryType: '',
  subject: '',
  message: '',
  privacy_policy: false,
};

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!window.__turnstileScriptPromise) {
    window.__turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return window.__turnstileScriptPromise;
}

export default function ContactForm() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY_FORM);
  const [messagePlaceholder, setMessagePlaceholder] = useState(defaultPlaceholder());
  const [status, setStatus] = useState({ loading: false, message: '', error: false });
  // Honeypot: hidden from people; bots that fill it are dropped server-side.
  const [website, setWebsite] = useState('');
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const turnstileToken = useRef('');

  useEffect(() => {
    if (!turnstileSiteKey) return undefined;
    let cancelled = false;
    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !turnstileRef.current) return;
        turnstileWidgetId.current = turnstile.render(turnstileRef.current, {
          sitekey: turnstileSiteKey,
          action: 'contact',
          callback: (token) => {
            turnstileToken.current = token;
          },
          'expired-callback': () => {
            turnstileToken.current = '';
          },
          'error-callback': () => {
            turnstileToken.current = '';
          },
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (window.turnstile && turnstileWidgetId.current !== null) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [turnstileSiteKey]);

  const resetTurnstile = () => {
    turnstileToken.current = '';
    if (window.turnstile && turnstileWidgetId.current !== null) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
  };

  useEffect(() => {
    const slug = searchParams.get('service');
    const option = getContactServiceBySlug(slug);
    if (option) {
      setForm((prev) => ({
        ...prev,
        service: option.value,
        subject: option.subject,
      }));
      setMessagePlaceholder(option.messagePlaceholder);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'service') {
      const option = getContactServiceBySlug(value);
      setForm((prev) => ({
        ...prev,
        service: value,
        subject: option?.subject || prev.subject,
      }));
      if (option?.messagePlaceholder) {
        setMessagePlaceholder(option.messagePlaceholder);
      }
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, message: '', error: false });

    try {
      const res = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          website,
          'cf-turnstile-response': turnstileToken.current,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        trackGenerateLead({
          service: resolveContactServiceValue(form.service, searchParams.get('service')),
          inquiryType: form.inquiryType || '',
          role: form.role || '',
          companySize: form.companySize || '',
        });
        setStatus({ loading: false, message: data.message || 'Submitted!', error: false });
        setForm(EMPTY_FORM);
        setMessagePlaceholder(defaultPlaceholder());
      } else {
        setStatus({
          loading: false,
          message: data.error || data.message || 'Submission failed.',
          error: true,
        });
      }
    } catch {
      setStatus({ loading: false, message: 'Network error. Please try again.', error: true });
    }
    // Turnstile tokens are single-use: get a fresh one for any later submit.
    resetTurnstile();
  };

  return (
    <>
      <SectionSurface
        variant="wash"
        as="section"
        decor="none"
        underNav
        bleed="bottom"
        flowBottom
        overlap
      >
        <div className="container-x section-tight pb-0">
          <div className="mx-auto max-w-3xl text-center">
            <p className="eyebrow">{CONTACT_HERO.eyebrow}</p>
            <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-ink-900">
              {CONTACT_HERO.title}
            </h1>
            <p className="mt-5 text-lg text-ink-600 leading-relaxed">{CONTACT_HERO.body}</p>
            <p className="mt-4 text-base text-ink-500 leading-relaxed max-w-2xl mx-auto">
              {CONTACT_HERO.platforms}
            </p>
          </div>
        </div>
      </SectionSurface>

      <SectionSurface variant="grid" decor="mesh" as="section" bleed="both" flowTop flowBottom overlap className="pb-16">
        <div className="container-x section-tight pt-0">
          <div className="card overflow-hidden shadow-md border-ink-200/90">
            <h2 className="sr-only">Contact Us</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3">
              <div className="bg-gradient-to-br from-brand-50/40 via-surface-muted to-accent-50/30 px-6 py-10 sm:px-10 xl:p-12 border-b lg:border-b-0 lg:border-r border-ink-200">
                <p className="eyebrow">Structured discovery</p>
                <h3 className="mt-3 text-2xl font-semibold text-ink-900">
                  Semantic readiness consultation
                </h3>
                <p className="mt-3 text-base text-ink-600 leading-relaxed">
                  Select the inquiry area that best matches your initiative. A senior architect will align
                  scope to semantic foundations, Snowflake modernization, conversational BI, or decision intelligence
                  on your existing platforms.
                </p>
                <div className="mt-8">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-ink-500">Email</h4>
                  <div className="mt-2 flex items-center text-sm text-ink-800">
                    <EnvelopeIcon className="w-5 h-5 text-brand-600 mr-2" />
                    <a href="mailto:info@myorglab.com" className="inline-flex min-h-[24px] items-center hover:text-brand-600 transition-colors">
                      info@myorglab.com
                    </a>
                  </div>
                </div>
                <div className="mt-6">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-ink-500">Phone</h4>
                  <div className="mt-2 flex items-center text-sm text-ink-800">
                    <PhoneIcon className="w-5 h-5 text-brand-600 mr-2" />
                    <a href="tel:+16177662836" className="inline-flex min-h-[24px] items-center hover:text-brand-600 transition-colors">
                      (617) 766-2836
                    </a>
                  </div>
                </div>
                <div className="mt-6">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-ink-500">
                    Headquarters
                  </h4>
                  <div className="mt-2 flex items-start text-sm text-ink-800">
                    <MapPinIcon className="w-5 h-5 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                    <address className="not-italic leading-relaxed">
                      71 Commercial St, Suite 11
                      <br />
                      Boston, Massachusetts 02109
                      <br />
                      United States
                    </address>
                  </div>
                </div>
                <p className="mt-8 text-xs text-ink-500">
                  We typically respond within one business day.
                </p>
              </div>

              <div className="px-6 py-10 sm:px-10 lg:col-span-2 xl:p-12 bg-white/90">
                <h3 className="text-xl font-semibold text-ink-900">Tell us about your initiative</h3>
                <form
                  className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-ink-800">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      required
                      value={form.firstName}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-ink-200 px-4 py-3 text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-ink-800">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      required
                      value={form.lastName}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-ink-200 px-4 py-3 text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-ink-800">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-ink-200 px-4 py-3 text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="company" className="block text-sm font-medium text-ink-800">
                      Company Name
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      required
                      value={form.company}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-ink-200 px-4 py-3 text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="role" className="block text-sm font-medium text-ink-800">
                      Role / Position
                    </label>
                    <select
                      id="role"
                      name="role"
                      required
                      value={form.role}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-ink-200 bg-white px-4 py-3 text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    >
                      <option value="">Select...</option>
                      <option value="executive">Executive / C-Level</option>
                      <option value="manager">Manager / Director</option>
                      <option value="analyst">Analyst / Specialist</option>
                      <option value="developer">IT / Developer</option>
                      <option value="consultant">Consultant</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="companySize" className="block text-sm font-medium text-ink-800">
                      Company Size
                    </label>
                    <select
                      id="companySize"
                      name="companySize"
                      required
                      value={form.companySize}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-ink-200 bg-white px-4 py-3 text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    >
                      <option value="">Select...</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501-1000">501-1000 employees</option>
                      <option value="1001+">1001+ employees</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="service" className="block text-sm font-medium text-ink-800">
                      Service area
                    </label>
                    <select
                      id="service"
                      name="service"
                      required
                      value={form.service}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    >
                      <option value="">Select…</option>
                      {CONTACT_SERVICE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="subject" className="block text-sm font-medium text-ink-800">
                      Subject (optional)
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Pre-filled from service selection when applicable"
                      className="mt-1 block w-full rounded-md border border-ink-200 px-4 py-3 text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="inquiryType" className="block text-sm font-medium text-ink-800">
                      Inquiry type
                    </label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      required
                      value={form.inquiryType}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-ink-200 bg-white px-4 py-3 text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    >
                      <option value="">Select...</option>
                      <option value="pricing">Pricing / engagement model</option>
                      <option value="partnership">Partnership opportunity</option>
                      <option value="general">General question</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="message" className="block text-sm font-medium text-ink-800">
                      Context
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      placeholder={messagePlaceholder}
                      required
                      value={form.message}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-ink-200 px-4 py-3 text-ink-900 shadow-xs focus:border-brand-400 focus:ring-4 focus:ring-brand-700"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <div className="flex items-start">
                      <div className="flex h-5 items-center">
                        <input
                          id="privacy_policy"
                          name="privacy_policy"
                          type="checkbox"
                          required
                          checked={form.privacy_policy}
                          onChange={(e) => setForm({ ...form, privacy_policy: e.target.checked })}
                          className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-700"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="privacy_policy" className="font-medium text-ink-800">
                          I agree to the{' '}
                          <a href="/privacy-policy" className="text-brand-600 hover:underline">
                            Privacy Policy
                          </a>
                          .
                        </label>
                      </div>
                    </div>
                  </div>
                  <div
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
                  >
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      data-testid="contact-honeypot"
                    />
                  </div>
                  {turnstileSiteKey && (
                    <div className="sm:col-span-2" ref={turnstileRef} data-testid="contact-turnstile" />
                  )}
                  <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row sm:justify-end">
                    <button type="submit" disabled={status.loading} className="btn-primary btn-lg w-full sm:w-auto">
                      {status.loading ? 'Submitting…' : 'Submit inquiry'}
                    </button>
                  </div>
                  {status.message && (
                    <div
                      className={`sm:col-span-2 mt-4 text-sm ${status.error ? 'text-red-500' : 'text-green-600'}`}
                    >
                      {status.message}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </SectionSurface>
    </>
  );
}
